/**
 * HotkeySettingsSection - Configure keyboard shortcuts for production stations
 *
 * Follows WorkflowSettingsSection pattern:
 * - embedded/standalone modes
 * - Save button appears when changes exist
 * - Loading/error states
 * - Reset to defaults with confirmation
 *
 * Actions grouped by station (Catcher, Printer, Packer).
 * Live conflict detection (within-station only) shown as alert + save disabled.
 */

import { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  ScanLine,
  Printer,
  PackageCheck,
  Save,
  Loader2,
  AlertTriangle,
  RotateCcw,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { useHotkeySettings } from '../hotkeys/useHotkeySettings';
import { HotkeyCapture } from '../hotkeys/HotkeyCapture';
import {
  STATION_GROUPS,
  HOTKEY_ACTION_LABELS,
  DEFAULT_HOTKEY_MAP,
  type HotkeyAction,
  type HotkeyBinding,
  type HotkeyMap,
  type StationGroup,
} from '../hotkeys/hotkey-types';

const STATION_ICONS: Record<StationGroup, typeof ScanLine> = {
  catcher: ScanLine,
  printer: Printer,
  packer: PackageCheck,
};

interface HotkeySettingsSectionProps {
  embedded?: boolean;
}

/**
 * Detect duplicate bindings within the same station.
 * Returns list of conflict descriptions, or empty if no conflicts.
 */
function detectConflicts(draft: HotkeyMap): string[] {
  const conflicts: string[] = [];

  for (const [, group] of Object.entries(STATION_GROUPS)) {
    const seen = new Map<string, string>();
    for (const action of group.actions) {
      const binding = draft[action];
      if (!binding) continue;

      const key =
        binding.type === 'combo'
          ? `combo:${[...binding.modifiers].sort().join('+')}+${binding.key.toLowerCase()}`
          : `hold:${binding.key.toLowerCase()}`;

      const existing = seen.get(key);
      if (existing) {
        const existingLabel = HOTKEY_ACTION_LABELS[existing as HotkeyAction]?.label ?? existing;
        const actionLabel = HOTKEY_ACTION_LABELS[action]?.label ?? action;
        conflicts.push(`${group.label}: "${existingLabel}" and "${actionLabel}" share the same binding`);
      }
      seen.set(key, action);
    }
  }
  return conflicts;
}

/** Check if a binding matches the default for that action */
function isDefaultBinding(action: HotkeyAction, binding: HotkeyBinding | null): boolean {
  return JSON.stringify(binding) === JSON.stringify(DEFAULT_HOTKEY_MAP[action]);
}

export function HotkeySettingsSection({ embedded = false }: HotkeySettingsSectionProps) {
  const { hotkeyMap, isLoading, isError, lastError, updateHotkeys, isUpdating } = useHotkeySettings();
  const [draft, setDraft] = useState<HotkeyMap>({ ...DEFAULT_HOTKEY_MAP });
  const [hasChanges, setHasChanges] = useState(false);

  // Sync draft with server data
  useEffect(() => {
    if (hotkeyMap) {
      setDraft({ ...hotkeyMap });
      setHasChanges(false);
    }
  }, [hotkeyMap]);

  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflictList, setConflictList] = useState<string[]>([]);

  const handleBindingChange = (action: HotkeyAction, binding: HotkeyBinding | null) => {
    setDraft((prev) => ({ ...prev, [action]: binding }));
    setHasChanges(true);
  };

  const handleSave = useCallback(async () => {
    // Check for conflicts and show dialog if any
    const conflicts = detectConflicts(draft);
    if (conflicts.length > 0) {
      setConflictList(conflicts);
      setShowConflictDialog(true);
      return;
    }

    const result = await updateHotkeys(draft as Record<string, HotkeyBinding | null>);
    if (result.success) {
      toast.success('Hotkey settings saved');
      setHasChanges(false);
    } else {
      toast.error(result.error ?? 'Failed to save hotkey settings');
    }
  }, [draft, updateHotkeys]);

  const handleReset = () => {
    setDraft({ ...DEFAULT_HOTKEY_MAP });
    setHasChanges(true);
  };

  const isDirty = (action: HotkeyAction): boolean => {
    const current = hotkeyMap[action];
    const draftVal = draft[action];
    return JSON.stringify(current) !== JSON.stringify(draftVal);
  };

  // Save button
  const saveButton = hasChanges ? (
    <Button
      onClick={handleSave}
      disabled={isUpdating}
      className="h-12 gap-2"
    >
      {isUpdating ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Save className="h-4 w-4" />
      )}
      Save Changes
    </Button>
  ) : null;

  const content = (
    <div className="space-y-6">
      {/* Error State */}
      {isError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Failed to load hotkey settings</AlertTitle>
          <AlertDescription>
            {lastError ?? 'Unknown error'}
          </AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading hotkey settings...</span>
        </div>
      )}

      {!isLoading && !isError && (
        <>
          <p className="text-sm text-muted-foreground">
            Configure keyboard shortcuts for each production station. Changes apply to all users after saving.
          </p>

          {/* Scanner safety help */}
          <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 rounded-lg p-3">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
            <span>
              Combo shortcuts (Ctrl+key, Alt+key) are recommended for scanner safety.
              Hold shortcuts require holding a key for 300ms+ and are safe because scanners release keys in under 100ms.
            </span>
          </div>

          {/* Station groups */}
          {(Object.entries(STATION_GROUPS) as Array<[StationGroup, (typeof STATION_GROUPS)[StationGroup]]>).map(
            ([stationKey, group]) => {
              const Icon = STATION_ICONS[stationKey];
              return (
                <div key={stationKey} className="border rounded-lg p-4 space-y-3 bg-muted/20">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    <span className="font-medium text-sm">{group.label}</span>
                  </div>
                  <Separator />
                  <div className="space-y-3">
                    {group.actions.map((action) => {
                      const meta = HOTKEY_ACTION_LABELS[action];
                      const isDefault = isDefaultBinding(action, draft[action]);
                      return (
                        <div
                          key={action}
                          className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 min-h-[44px]"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{meta.label}</span>
                              {isDefault && (
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                  Default
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground">{meta.description}</div>
                          </div>
                          <HotkeyCapture
                            value={draft[action]}
                            onChange={(binding) => handleBindingChange(action, binding)}
                            actionLabel={meta.label}
                            isDirty={isDirty(action)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            },
          )}

          {/* Reset to Defaults */}
          <div className="flex justify-between items-center pt-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" className="text-muted-foreground gap-1.5">
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reset to Defaults
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset hotkey settings?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will restore all keyboard shortcuts to their default values. You will need to save to apply.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleReset}>Reset</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            {saveButton}
          </div>
        </>
      )}

      {/* ARIA live region */}
      <div role="status" aria-live="polite" className="sr-only">
        {isUpdating && 'Saving hotkey settings...'}
        {hasChanges && 'You have unsaved changes'}
      </div>
    </div>
  );

  // Conflict dialog — shown when user tries to save with duplicate bindings
  const conflictDialog = (
    <AlertDialog open={showConflictDialog} onOpenChange={setShowConflictDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Can&apos;t Save: Hotkey Conflicts
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                Two or more actions in the same station share the same keyboard shortcut.
                Each action needs a unique binding.
              </p>
              <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-3 space-y-2">
                {conflictList.map((c, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <span className="text-destructive font-medium shrink-0">Conflict:</span>
                    <span>{c}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">
                Change one of the conflicting bindings to a different key combination, then try saving again.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction>Got it</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  // Embedded mode (inside accordion)
  if (embedded) {
    return (
      <>
        <div className="space-y-4">
          {saveButton && (
            <div className="flex justify-end">
              {saveButton}
            </div>
          )}
          {content}
        </div>
        {conflictDialog}
      </>
    );
  }

  // Standalone mode
  return (
    <>
      {content}
      {conflictDialog}
    </>
  );
}
